import React, { useCallback, useEffect, useRef, useState } from 'react'
import Intercom from '@intercom/messenger-js-sdk'

import { Drawer, DrawerContent, DrawerOverlay } from '@chakra-ui/react'
import { Box, BoxProps, Flex } from '@chakra-ui/react'
import { navigate } from 'gatsby-link'
import { useMatch, useLocation } from '@reach/router'

import Header from './header'
import Sidebar from './sidebar/sidebar'
import ClinicSwitchToolbar from './clinic-switch-toolbar'
import { useUserContext } from '~config/user-context'
import { useClinic } from '~contexts/clinic-context'
import TutorialModal from './tutorial-modal'
import { UserRoutes } from '~config/routes'
import LoadingView from '../loading-view'
import DetailDrawers from './detail-drawers'
import MobileSidebar from './mobile-drawer'
import { getUserFullName } from '~utils/helpers'
import { FaviconHandler } from '../favicon-handler'
import InactivityModal from '../modals/inactivity-modal'
import {
    DARK_BACKGROUND_PAGES,
    EXCLUDED_DARK_BACKGROUND_PAGES,
} from './constants'
import ChatPopovers from '~components/whiteboard/chat-popovers'
import TeamChatPopovers from '~components/team-chat/popovers/team-chat-popovers'
import UniversalSearchModal from '~components/search/universal-search-modal'
import AskChckvetDock from '~components/ask-chckvet/ask-chckvet-dock'
import { ASK_CHCKVET_MAIN_ATTR } from '~components/ask-chckvet/ask-chckvet-dom'
import { AskChckvetDockProvider } from '~components/ask-chckvet/ask-chckvet-context'
import { useGlobalHotkey } from '~utils/hooks/use-global-hotkey'

type LayoutProps = BoxProps & {
    children: React.ReactNode
    minH?: string | number
    gridGap?: number
}

const Layout: React.FC<LayoutProps> = ({ children, ...boxProps }) => {
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
    const [searchOpen, setSearchOpen] = useState<boolean>(false)
    const { pathname } = useLocation()
    const matchAuth = pathname.includes('/auth')
    const matchClinicSelect = useMatch(UserRoutes.SelectClinic)
    const matchIntegrationUpload = useMatch(UserRoutes.IntegrationUpload)
    const matchRhapsodyWidget = useMatch(UserRoutes.RhapsodyWidget)
    const shouldSkipAppLayout = Boolean(
        matchAuth ||
        matchClinicSelect ||
        matchIntegrationUpload ||
        matchRhapsodyWidget,
    )
    const matchesDarkBackground = DARK_BACKGROUND_PAGES.some(
        route =>
            pathname.includes(route) &&
            !EXCLUDED_DARK_BACKGROUND_PAGES.some(excludedRoute =>
                pathname.includes(excludedRoute),
            ),
    )
    const isChatsPage = pathname.includes('/chats')

    const { user, userId, intercomJwt, isLoading } = useUserContext()
    const { loading, clinic, clinicGroup, isSwitchingClinic } = useClinic()

    const firstLoad = useRef(true)

    const openSearch = useCallback(() => setSearchOpen(true), [])
    const closeSearch = useCallback(() => setSearchOpen(false), [])

    useEffect(() => {
        if (
            !shouldSkipAppLayout &&
            !user &&
            !userId &&
            !isLoading &&
            !firstLoad.current
        ) {
            navigate(UserRoutes.Auth)
        }
        firstLoad.current = false
    }, [user, isLoading, userId, shouldSkipAppLayout])

    useEffect(() => {
        setDrawerOpen(false)
    }, [pathname])

    Intercom({
        app_id: 'c80iqoum',
        intercom_user_jwt: intercomJwt,
        name: user ? getUserFullName(user) : 'Unknown User',
        email: user?.email,
        company: {
            id: clinic?.id || '',
            name: clinic?.name || '',
        },
        alignment: 'left',
        created_at: user?.created_at,
        session_duration: 86400000,
        hide_default_launcher: isChatsPage,
    })

    // Only take over the whole screen on the initial load (no user yet, or no
    // clinic/group to render). Once the shell has painted for this user, keep
    // sidebar + header mounted through query refetches and page changes — a
    // full-screen loader is what reads as the "white flash" on navigate.
    const waitingForInitialSession =
        isLoading || (loading && !clinic && !clinicGroup)
    const showFullLoader =
        renderedShellForUserId !== userId && waitingForInitialSession
    const isSearchEnabled =
        !showFullLoader &&
        !matchAuth &&
        !matchClinicSelect &&
        !matchRhapsodyWidget

    useGlobalHotkey({
        key: 'k',
        modifier: 'mod',
        enabled: isSearchEnabled,
        onKeyDown: openSearch,
    })

    if (showFullLoader) return <LoadingView h="100dvh" w="100dvw" />

    if (shouldSkipAppLayout) return <Box>{children}</Box>

    if (userId) {
        renderedShellForUserId = userId
    }

    return (
        <AskChckvetDockProvider>
            <Flex
                w="100vw"
                minW={{ base: 'none', lg: '1024px' }}
                maxW="100vw"
                bg={matchesDarkBackground ? 'gray.100' : 'white'}
                height="100dvh"
                overflowY="hidden"
            >
                {/* minW={0} on both: a flex item won't shrink below its
                    content by default, so a very wide page stretches these
                    wrappers and pushes the sidebar and header off-screen
                    instead of scrolling inside the content area below. */}
                <Flex flex={1} minW={0}>
                    <Sidebar />
                    <Flex flex={1} minW={0} flexDir="column">
                        <Header
                            onOpenSidebar={() => setDrawerOpen(true)}
                            onOpenSearch={openSearch}
                        />
                        <ClinicSwitchToolbar />
                        <Box
                            flex={1}
                            minH={0}
                            // minW={0} is the horizontal counterpart: without
                            // it a flex item won't shrink below its content, so
                            // a very wide page (a spreadsheet-style table)
                            // stretches the whole shell and pushes the header
                            // and sidebar off-screen instead of scrolling here.
                            minW={0}
                            overflowY="scroll"
                            p={{ base: 0, lg: matchesDarkBackground ? 2 : 0 }}
                            {...boxProps}
                            {...{ [ASK_CHCKVET_MAIN_ATTR]: '' }}
                        >
                            {isSwitchingClinic ? (
                                <LoadingView h="100%" w="100%" />
                            ) : (
                                children
                            )}
                        </Box>
                    </Flex>
                    {/* Sibling of the page column, so opening the dock
                        narrows the content instead of covering it. */}
                    <AskChckvetDock />
                </Flex>
            </Flex>
            <TutorialModal />
            <UniversalSearchModal isOpen={searchOpen} onClose={closeSearch} />
            <InactivityModal />
            <Box display={{ lg: 'none' }}>
                <Drawer
                    isOpen={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                >
                    <DrawerOverlay>
                        <DrawerContent>
                            <MobileSidebar />
                        </DrawerContent>
                    </DrawerOverlay>
                </Drawer>
            </Box>
            <DetailDrawers />
            <FaviconHandler />
            <ChatPopovers />
            <TeamChatPopovers />
        </AskChckvetDockProvider>
    )
}

export default Layout

/**
 * Survives layout remounts within a session so refetch/navigation never
 * replaces the app shell with the full-screen loader. Keyed by userId so a
 * new login still gets the initial loader.
 */
let renderedShellForUserId: string | null = null
